import { useEffect, useState } from 'react';

const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });

    const text = await response.text();
    const json = text ? (() => {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    })() : null;

    if (!response.ok) {
        throw new Error(json?.message || text || `${response.status} ${response.statusText}`);
    }

    return json;
};

const EMPTY_FORM = { name: '', description: '', is_active: true };

export default function BiensCategoriesTab() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchJson('/api/biens/categories');
            setCategories(data || []);
        } catch (e) {
            setError(e.message || 'Erreur de chargement');
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { setError('Le nom est requis.'); return; }
        setError('');
        setLoading(true);
        try {
            if (editing) {
                const updated = await fetchJson(`/api/biens/categories/${editing.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(form),
                });
                setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            } else {
                const created = await fetchJson('/api/biens/categories', {
                    method: 'POST',
                    body: JSON.stringify(form),
                });
                setCategories((prev) => [created, ...prev]);
            }
            cancelEdit();
        } catch (e) {
            setError(e.message || 'Erreur');
        }
        setLoading(false);
    };

    const startEdit = (c) => {
        setEditing(c);
        setForm({ name: c.name, description: c.description || '', is_active: !!c.is_active });
    };

    const cancelEdit = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setError('');
    };

    const remove = async (c) => {
        if (!confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
        try {
            await fetchJson(`/api/biens/categories/${c.id}`, { method: 'DELETE' });
            setCategories((prev) => prev.filter((x) => x.id !== c.id));
        } catch (e) {
            setError('Impossible de supprimer : ' + (e.message || 'Erreur'));
        }
    };

    return (
        <div className="p-6">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">

                {/* ── En-tête ── */}
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
                    <div>
                        <h2 className="text-lg font-medium text-slate-900">Catégories des biens</h2>
                        <p className="mt-0.5 text-sm text-slate-500">Gérer les catégories utilisées par les biens.</p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500">
                        {categories.length} catégorie{categories.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* ── Formulaire ── */}
                <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
                    <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                        {editing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                    </p>
                    <form onSubmit={save} className="flex flex-wrap items-center gap-2">
                        <input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Nom *"
                            required
                            className="h-9 flex-1 min-w-[160px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                        <input
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Description"
                            className="h-9 flex-1 min-w-[160px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                        {/* Toggle actif */}
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={form.is_active}
                                onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 ${form.is_active ? 'bg-emerald-500' : 'bg-slate-200'}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-4' : 'translate-x-0'}`}
                                />
                            </button>
                            Actif
                        </label>
                        {/* Boutons */}
                        <div className="flex items-center gap-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                {editing ? 'Mettre à jour' : 'Créer'}
                            </button>
                            {editing && (
                                <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-600 hover:bg-slate-50"
                                >
                                    Annuler
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* ── Message d'erreur ── */}
                {error && (
                    <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                        {error}
                    </div>
                )}

                {/* ── Tableau ── */}
                <div className="overflow-x-auto px-6 pb-6 pt-4">
                    {loading ? (
                        <div className="py-10 text-center text-sm text-slate-400">Chargement…</div>
                    ) : categories.length === 0 ? (
                        <div className="py-10 text-center">
                            <p className="text-sm text-slate-400">Aucune catégorie pour l'instant.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr>
                                    {['Nom', 'Description', 'Statut', 'Actions'].map((h) => (
                                        <th key={h} className="border-b border-slate-100 pb-2 pt-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {categories.map((c) => (
                                    <tr key={c.id} className="group">
                                        <td className="py-2.5 pr-4 font-medium text-slate-800">{c.name}</td>
                                        <td className="py-2.5 pr-4 text-slate-500">{c.description || '—'}</td>
                                        <td className="py-2.5 pr-4">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {c.is_active && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                )}
                                                {c.is_active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="py-2.5">
                                            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                                <button
                                                    onClick={() => startEdit(c)}
                                                    title="Modifier"
                                                    className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                </button>
                                                <button
                                                    onClick={() => remove(c)}
                                                    title="Supprimer"
                                                    className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

            </div>
        </div>
    );
}